import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Alert, Stack,
  MenuItem, TextField, Typography, Autocomplete,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../context/AuthContext';
import { useEmpresasActivas } from '../../hooks/useUsuarios';
import { usePeriodos } from '../../hooks/usePeriodos';
import { useCreateDDJJ, usePlanillas } from '../../hooks/useDDJJ';
import { formatCuit } from '../../utils/format';
import { empresaCoincide } from '../../utils/empresaSearch';

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  empresa_id: z.string().uuid('Seleccioná una empresa'),
  periodo_id: z.string().uuid('Seleccioná un período'),
});

type FormData = z.infer<typeof schema>;

// ── Props ─────────────────────────────────────────────────────────────────────

interface NuevaDDJJDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback con el ID de la planilla creada para navegar al detalle */
  onCreated: (planillaId: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function NuevaDDJJDialog({
  open,
  onClose,
  onCreated,
}: NuevaDDJJDialogProps) {
  const { effectiveRole, effectiveEmpresaId } = useAuth();
  const isAdminEmpresa = effectiveRole === 'admin_empresa';

  const { data: empresas = [] } = useEmpresasActivas();
  const { data: periodos = [] } = usePeriodos(false); // solo períodos activos
  const createDDJJ = useCreateDDJJ();

  // Confirmación de DDJJ adicional (#489): cuando ya existe una DDJJ para el
  // empresa+período elegido, guardamos los valores acá para pedir confirmación
  // antes de crear la adicional.
  const [pendingConfirm, setPendingConfirm] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { empresa_id: '', periodo_id: '' },
  });

  const empresaIdWatched = watch('empresa_id');

  // Planillas existentes para la empresa seleccionada
  // (para mostrar indicadores en el dropdown de períodos)
  const empresaIdParaQuery = empresaIdWatched || (isAdminEmpresa ? effectiveEmpresaId : null);
  const { data: planillasExistentes = [] } = usePlanillas(
    empresaIdParaQuery ?? undefined
  );

  // Mapa periodo_id → array de DDJJs existentes
  const planillasPorPeriodo = useMemo(() => {
    const map: Record<string, { numero: number; estado: string }[]> = {};
    for (const p of planillasExistentes) {
      if (!map[p.periodo_id]) map[p.periodo_id] = [];
      map[p.periodo_id].push({ numero: p.numero, estado: p.estado });
    }
    return map;
  }, [planillasExistentes]);

  // Para admin_empresa: pre-seleccionar su empresa
  useEffect(() => {
    if (open) {
      reset({ empresa_id: effectiveEmpresaId ?? '', periodo_id: '' });
      setPendingConfirm(null);
      if (isAdminEmpresa && effectiveEmpresaId) {
        setValue('empresa_id', effectiveEmpresaId);
      }
    }
  }, [open, effectiveEmpresaId, isAdminEmpresa, reset, setValue]);

  // Creación efectiva de la DDJJ.
  const doCreate = async (values: FormData) => {
    try {
      const planilla = await createDDJJ.mutateAsync({
        empresa_id: values.empresa_id,
        periodo_id: values.periodo_id,
      });
      onCreated(planilla.id);
    } catch (_err) {
      // el error se muestra vía createDDJJ.error
    }
  };

  const onSubmit = async (values: FormData) => {
    // Si ya existe al menos una DDJJ para este empresa+período, pedir
    // confirmación antes de crear la adicional (#489). La 1ra no avisa.
    const existentes = planillasPorPeriodo[values.periodo_id] ?? [];
    if (existentes.length > 0) {
      setPendingConfirm(values);
      return;
    }
    await doCreate(values);
  };

  // El usuario confirma crear la DDJJ adicional.
  const confirmarAdicional = async () => {
    if (!pendingConfirm) return;
    const values = pendingConfirm;
    setPendingConfirm(null);
    await doCreate(values);
  };

  const apiError = createDDJJ.error as Error | null;

  // Mensaje amigable para el error de borrador duplicado (índice parcial RN-22)
  const errorMessage = apiError?.message?.includes('planillas_ddjj_one_borrador')
    ? 'Ya existe una DDJJ en borrador para este período. Confirmala o eliminala antes de crear una nueva.'
    : apiError?.message ?? null;

  // Indicador textual para un período con DDJJs existentes
  const getPeriodoSufijo = (periodoId: string): string => {
    const lista = planillasPorPeriodo[periodoId];
    if (!lista || lista.length === 0) return '';
    const labels = lista.map((d) => `#${d.numero} ${d.estado}`).join(', ');
    return ` — DDJJ existente: ${labels}`;
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>Nueva Declaración Jurada</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errorMessage && (
              <Alert severity="error">{errorMessage}</Alert>
            )}

            {/* Empresa (solo visible para admin/operador): buscable por
                nombre o CUIT (#493) */}
            {!isAdminEmpresa && (
              <Controller
                name="empresa_id"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={empresas}
                    value={empresas.find((e) => e.id === field.value) ?? null}
                    onChange={(_, val) => field.onChange(val?.id ?? '')}
                    getOptionLabel={(opt) => opt.razon_social}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    filterOptions={(opts, { inputValue }) =>
                      opts.filter((e) => empresaCoincide(e, inputValue))
                    }
                    noOptionsText="Sin resultados"
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empresa"
                        required
                        placeholder="Buscar por nombre o CUIT…"
                        error={!!errors.empresa_id}
                        helperText={errors.empresa_id?.message}
                      />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={opt.id}>
                        <Stack spacing={0}>
                          <Typography variant="body2">{opt.razon_social}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCuit(opt.cuit)}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                  />
                )}
              />
            )}

            {/* Período */}
            <Controller
              name="periodo_id"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Período"
                  required
                  error={!!errors.periodo_id}
                  helperText={errors.periodo_id?.message}
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Seleccioná un período…</em>
                  </MenuItem>
                  {periodos.map((p) => {
                    const sufijo = getPeriodoSufijo(p.id);
                    return (
                      <MenuItem key={p.id} value={p.id}>
                        <Stack direction="column" spacing={0}>
                          <Typography variant="body2">
                            {p.periodo}
                            {p.excepcional ? ' (excepcional)' : ''}
                            {p.descripcion ? ` — ${p.descripcion}` : ''}
                          </Typography>
                          {sufijo && (
                            <Typography variant="caption" color="text.secondary">
                              {sufijo}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
            />

            <Alert severity="info" sx={{ fontSize: 13 }}>
              El sistema cargará automáticamente los afiliados con relación
              laboral activa en la empresa. Si ya existe una DDJJ para el período,
              la nueva se creará como <strong>DDJJ adicional</strong>.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
          >
            {isSubmitting ? 'Creando…' : 'Crear DDJJ'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>

    {/* Confirmación de DDJJ adicional (#489): ya existe al menos una DDJJ para
        este empresa+período. Se pide confirmación explícita antes de crear. */}
    <Dialog open={!!pendingConfirm} onClose={() => setPendingConfirm(null)} maxWidth="sm" fullWidth>
      <DialogTitle>Ya existe una DDJJ para este período</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Alert severity="warning">
            {(() => {
              const emp = empresas.find((e) => e.id === pendingConfirm?.empresa_id);
              const per = periodos.find((p) => p.id === pendingConfirm?.periodo_id);
              const existentes = pendingConfirm
                ? planillasPorPeriodo[pendingConfirm.periodo_id] ?? []
                : [];
              return (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    {emp ? <strong>{emp.razon_social}</strong> : 'La empresa'} ya tiene{' '}
                    {existentes.length === 1 ? 'una DDJJ' : `${existentes.length} DDJJ`} para el
                    período <strong>{per?.periodo ?? ''}</strong>:
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0}>
                    {existentes.map((d) => (
                      <li key={d.numero}>
                        <Typography variant="body2">
                          DDJJ #{d.numero} — {d.estado}
                        </Typography>
                      </li>
                    ))}
                  </Stack>
                  <Typography variant="body2">
                    Si continuás, se creará una <strong>DDJJ adicional</strong>.
                  </Typography>
                </Stack>
              );
            })()}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setPendingConfirm(null)} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={confirmarAdicional}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
        >
          {isSubmitting ? 'Creando…' : 'Crear DDJJ adicional'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
